import { useField } from 'formik'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React from 'react'
import JsonEditor from '../../pages/home/secure/policy_feature/JsonEditor'
import TextField from '../TextField/TextField'

const propTypes = {
  /** Formik field name (dot paths supported) */
  id: PropTypes.string.isRequired,
  /** Rows for plain-string fallback (XML / non-JSON blobs). */
  rows: PropTypes.number,
  containerClassName: PropTypes.string,
  className: PropTypes.string,
  /** Root label in json-edit-react tree */
  rootName: PropTypes.string,
  /** When true, show validation errors even if the field was not blurred yet. */
  showErrorWhenInvalid: PropTypes.bool,
  disabled: PropTypes.bool,
}

function isJsonTreeRoot(value) {
  return value !== null && typeof value === 'object'
}

/**
 * JSON field for Formik: uses shared {@link JsonEditor} (json-edit-react) for objects and arrays.
 * Plain strings (e.g. VPN XML) use a monospace textarea via {@link TextField}.
 */
function FormikJsonTextAreaField(props) {
  const {
    id,
    rows = 12,
    containerClassName = '',
    className = '',
    rootName = 'Value',
    showErrorWhenInvalid = false,
    disabled = false,
  } = props ?? {}
  const [field, meta, helpers] = useField(id)
  const v = field.value

  const displayError = (showErrorWhenInvalid || meta.touched) && meta.error ? meta.error : ''
  const errorsArr = displayError ? [displayError] : []

  if (v !== undefined && v !== null && typeof v === 'string') {
    return (
      <div className={clsx(containerClassName)}>
        <TextField
          id={id}
          name={id}
          as="textarea"
          rows={rows}
          value={v}
          onChange={(e) => helpers.setValue(e.target.value)}
          onBlur={() => helpers.setTouched(true)}
          errors={errorsArr}
          containerClassName=""
          className={clsx('font-monospace small', className)}
          disabled={disabled}
        />
      </div>
    )
  }

  const treeData = isJsonTreeRoot(v) ? v : {}

  if (disabled) {
    return (
      <TextField
        id={id}
        name={id}
        as="textarea"
        rows={rows}
        value={JSON.stringify(treeData, null, 2)}
        containerClassName={containerClassName}
        className={clsx('font-monospace small', className)}
        disabled
      />
    )
  }

  return (
    <div className={clsx(containerClassName)}>
      <JsonEditor
        rootName={rootName}
        jsonEditorPayload={treeData}
        setJsonEditorPayload={(next) => {
          helpers.setValue(next)
          helpers.setTouched(true, false)
          helpers.setError(undefined)
        }}
        jsonEditorErrors={errorsArr}
      />
    </div>
  )
}

FormikJsonTextAreaField.propTypes = propTypes

export default FormikJsonTextAreaField
